import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { LayoutDashboard, Database, MapPin, BarChart3, History, Sun, Moon, Scale, Gavel, Building2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getTheme, toggleTheme } from '@/lib/theme';

const navItems = [
  { label: 'Dashboard', href: '/', icon: LayoutDashboard },
  { label: 'Sources', href: '/sources', icon: Database },
  { label: 'Parcels', href: '/parcels', icon: MapPin },
  { label: 'Crawl Runs', href: '/crawls', icon: History },
  { label: 'Coverage', href: '/coverage', icon: BarChart3 },
  { label: 'Courts', href: '/courts', icon: Scale },
  { label: 'Court Cases', href: '/courts/cases', icon: Gavel },
  { label: 'Court Sources', href: '/courts/sources', icon: Building2 },
  { label: 'Court Crawls', href: '/courts/crawls', icon: History },
  { label: 'Court Coverage', href: '/courts/coverage', icon: BarChart3 },
];

export function Sidebar() {
  const [theme, setTheme] = useState(getTheme);

  const handleToggle = () => {
    const next = toggleTheme();
    setTheme(next);
  };

  return (
    <aside className={cn(
      "w-60 flex flex-col relative overflow-hidden shadow-[4px_0_16px_rgba(0,0,0,0.12)]",
      theme === 'dark' ? 'bg-zinc-900' : ''
    )} style={theme === 'dark' ? undefined : { background: 'linear-gradient(135deg, oklch(72% 0.14 240) 0%, oklch(80% 0.08 225) 100%)' }}>
      {/* Decorative gradient line at top */}
      <div
        className="h-0.5 w-full shrink-0"
        style={{
          background: theme === 'dark'
            ? 'linear-gradient(90deg, #3f3f46, #52525b, #3f3f46)'
            : 'linear-gradient(90deg, oklch(55% 0.18 250), oklch(75% 0.14 195), oklch(55% 0.18 250))',
        }}
      />

      {/* Logo + title */}
      <div className="p-4 pb-3">
        <div className="flex items-center gap-2.5 mb-1">
          <svg viewBox="0 0 200 60" className="h-7 w-auto" xmlns="http://www.w3.org/2000/svg">
            <g>
              <g fill="currentColor">
                <path d="M77.2,40.3c-1.4,0-2.8,0-4.3,0c0-7.1,0-14.1,0-21.2c1,0,2.1-0.1,3.1,0c0.6,0.1,1,0.6,1.3,1 c2.7,3.4,5.4,6.8,8.1,10.2c0.5,0.7,1.1,1.3,1.7,2.1c0-4.5,0-8.9,0-13.3c1.5,0,2.9,0,4.3,0c0,7.1,0,14.1,0,21.2 c-1,0-2.1,0.1-3.1-0.1c-0.7-0.1-1.1-0.6-1.5-1.1c-2.9-3.6-5.8-7.3-8.7-10.9c-0.3-0.4-0.6-0.7-1-1.2C77.2,31.5,77.2,35.9,77.2,40.3z"/>
                <path d="M154.2,21.7c-0.5,0.7-0.9,1.5-1.5,2.2c-0.5,0.7-1.2,0.8-2,0.2c-1.3-0.8-2.7-1.3-4.3-1.3c-3.2,0-5.3,1.6-6,4.7 c-0.5,2-0.3,4,0.6,5.9c1.6,3.3,5.6,3.8,8.3,2.7c0.2-0.1,0.3-0.3,0.3-0.5c0-0.9,0-1.8,0-2.8c-0.7,0-1.4,0-2,0 c-0.8,0-1.1-0.3-1.1-1.1c0-0.8,0-1.6,0-2.5c1.3,0,2.5,0,3.8,0c1.2,0,2.5,0,3.6,0c0.2,0.3,0.1,0.6,0.1,0.8c0,2.5,0,5,0,7.4 c0,0.6-0.2,0.9-0.6,1.2c-2.5,1.6-5.2,2-8.1,1.8c-1.4-0.1-2.8-0.4-4.1-1c-4.6-2.1-6.9-6.4-6.2-11.5c0.8-5.5,4.8-9,10.4-9.1 c2.8-0.1,5.4,0.4,7.7,2.1C153.6,21.1,154,21.3,154.2,21.7z"/>
                <path d="M131.4,40.3c-4.6,0-9.1,0-13.6,0c0-7.1,0-14.1,0-21.2c4.5,0,9,0,13.6,0c0,1.2,0,2.4,0,3.8c-2.9,0-5.7,0-8.6,0 c0,1.7,0,3.2,0,4.9c2.2,0,4.4,0,6.6,0c0,1.3,0,2.4,0,3.7c-2.2,0-4.4,0-6.6,0c0,1.7,0,3.3,0,5c2.9,0,5.7,0,8.7,0 C131.4,37.8,131.4,39,131.4,40.3z"/>
                <path d="M96.2,22.9c0-1.3,0-2.6,0-3.9c5.6,0,11.2,0,16.9,0c0,1.3,0,2.5,0,3.8c-1.9,0-3.9,0-5.9,0c0,5.8,0,11.6,0,17.4 c-1.7,0-3.3,0-5,0c0-5.8,0-11.5,0-17.3C100.1,22.9,98.2,22.9,96.2,22.9z"/>
                <path d="M61.6,19.1c1.6,0,3.2,0,4.9,0c0,7.1,0,14.1,0,21.2c-1.6,0-3.2,0-4.9,0C61.6,33.2,61.6,26.2,61.6,19.1z"/>
                <path d="M170.9,31.4c1,0.6,1.6,1.4,2.1,2.3c1.3,2.1,2.6,4.3,3.9,6.4c-0.1,0.3-0.4,0.2-0.5,0.2c-1.2,0-2.4,0-3.6,0 c-1,0-1.8-0.4-2.3-1.3c-0.9-1.7-1.9-3.3-2.8-5c-0.9-1.6-1.1-1.7-3.1-1.5c0,2.5,0,5.1,0,7.7c-1.7,0-3.3,0-4.9,0c0-7,0-14.1,0-21.2 c0.1,0,0.2-0.1,0.3-0.1c2.8,0,5.6-0.1,8.4,0.1c0.9,0.1,1.7,0.2,2.5,0.5c2.4,0.8,3.9,2.3,4.2,4.8c0.3,2.6-0.6,4.8-2.8,6.3 C171.8,31,171.4,31.1,170.9,31.4z M168,28.8c1.5-0.4,2.3-1.6,2.2-3.2c0-1.5-0.9-2.4-2.4-2.7c-1.1-0.2-2.1-0.1-3.2-0.1 c0,2.1,0,4.2,0,6.3C165.8,29.1,166.9,29.1,168,28.8z"/>
                <path d="M200,40.3c-1.2,0-2.5,0-3.7,0c-0.9,0-1.4-0.5-1.7-1.3c-0.4-1.1-0.8-2.3-1.2-3.4c-2.7,0-5.4,0-8.1,0 c-0.4,1-0.7,2.1-1.1,3.2c-0.4,1.2-0.9,1.5-2.1,1.6c-1.1,0-2.3,0-3.5,0c0.7-1.7,1.3-3.2,1.9-4.8c2-5.2,4.1-10.5,6.1-15.7 c0.2-0.6,0.5-0.8,1.2-0.8c1.2,0.1,2.3,0,3.5,0c0.5,0,0.8,0.1,0.9,0.6c2.6,6.7,5.2,13.4,7.8,20.1C200,39.9,200.1,40,200,40.3z M189.3,23.6c-1,2.9-1.9,5.7-2.9,8.5c2,0,3.8,0,5.8,0C191.3,29.3,190.3,26.5,189.3,23.6z"/>
              </g>
              <linearGradient id="SVGID_1_" gradientUnits="userSpaceOnUse" x1="7.8297" y1="48.9863" x2="45.6839" y2="11.1321">
                <stop offset="0" style={{stopColor:'#0056F5'}}/>
                <stop offset="1" style={{stopColor:'#33CCF4'}}/>
              </linearGradient>
              <path fill="url(#SVGID_1_)" d="M53.2,16.8L27.7,1.2C27.5,1.1,27.2,1,27,1c-0.3,0-0.5,0.1-0.8,0.2L0.7,16.8C0.3,17,0,17.5,0,18v23.6 c0,0.5,0.3,1,0.7,1.3l25.5,15.9c0.2,0.2,0.5,0.2,0.8,0.2c0,0,0,0,0,0c0,0,0,0,0,0c0.3,0,0.6-0.1,0.8-0.2l25.5-15.9 c0.4-0.3,0.7-0.8,0.7-1.3V18C53.9,17.5,53.7,17,53.2,16.8z M27,32.1l-2.3-1.4l-2.9,1.8l3.7,2.3v20.1L4.4,41.7L27,27.9l2.3,1.4 l2.9-1.8l-3.7-2.2V5.2l21.1,12.9L27,32.1z M16.6,29.2l2.9-1.8L4.4,18.1L25.5,5.2v20.1L3,39V20.7L16.6,29.2z M50.9,39l-13.6-8.3 l-2.9,1.8l15.1,9.2L28.5,54.8V34.7l22.5-14V39z"/>
            </g>
          </svg>
        </div>
        <p className="text-xs font-bold text-blue-950/50 dark:text-sidebar-foreground/50 mt-1.5 pl-0.5">Public Records Pipeline</p>
      </div>

      <nav className="flex-1 px-2 space-y-0.5">
        {navItems.map((item) => (
          <NavLink
            key={item.href}
            to={item.href}
            end={item.href === '/'}
            className={({ isActive }) =>
              cn(
                'relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium',
                'transition-all duration-200',
                isActive
                  ? 'bg-white/20 text-blue-900 dark:bg-white/10 dark:text-blue-400'
                  : 'text-blue-950/70 hover:bg-white/15 hover:text-blue-950 hover:translate-x-0.5 dark:text-white/70 dark:hover:bg-white/10 dark:hover:text-white'
              )
            }
          >
            {({ isActive }) => (
              <>
                {isActive && (
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-blue-900 dark:bg-integra rounded-r-full" />
                )}
                <item.icon className={cn('h-5 w-5 shrink-0 transition-all duration-200', isActive && 'stroke-[2.5]')} />
                <span>{item.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Bottom: theme toggle + version */}
      <div className="p-3 space-y-2">
        <button
          onClick={handleToggle}
          className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
        >
          {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
        </button>
        <p className="text-xs text-sidebar-foreground/30 px-3">v0.2.0</p>
      </div>
    </aside>
  );
}
